import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, X, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { formatINR } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusChip = ({ status, triggeredPrice, unit }) => {
  if (status === "triggered") {
    return (
      <span className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.16em] text-[#2FB999]">
        <CheckCircle2 size={11} /> Triggered @ {formatINR(Math.round(triggeredPrice || 0))}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.16em] text-[#B87333]">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B87333] pulse-dot" style={{ color: "#B87333" }} />
      Active · watching
    </span>
  );
};

export const MyPriceAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/alerts`);
      setAlerts(r.data || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/alerts/${id}`);
      toast.success("Alert removed");
      setAlerts((s) => s.filter((a) => a.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove alert");
    }
  };

  if (loading) return null;
  if (alerts.length === 0) {
    return (
      <div data-testid="price-alerts" className="mt-16">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-2xl md:text-3xl">Price alerts</h2>
          <Link to="/trends" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] hover:underline">
            Open terminal <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="border border-dashed border-white/15 rounded-sm p-8 text-center bg-[#0a0a0a]">
          <Bell size={20} className="text-[#8A8A82] mx-auto mb-3" />
          <p className="font-body text-sm text-[#C9C9C2]">You haven't set any alerts yet.</p>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Head over to the <Link to="/trends" className="text-[#B87333] hover:underline">Price Pulse terminal</Link> and tap "Alert me" on any instrument.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="price-alerts" className="mt-16">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-2xl md:text-3xl">
          Price alerts <span className="text-[#8A8A82] text-lg">({alerts.length})</span>
        </h2>
        <Link to="/trends" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] hover:underline">
          Open terminal <ArrowUpRight size={12} />
        </Link>
      </div>
      <p className="font-body text-xs text-[#8A8A82] mb-4 max-w-xl">
        We'll email your account address the moment the Vinci desk crosses any of these targets — up or down.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {alerts.map((a) => (
          <div
            key={a.id}
            data-testid={`price-alert-${a.id}`}
            className="border border-white/10 rounded-sm p-4 bg-[#0a0a0a] flex items-start justify-between gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="font-serif text-base leading-tight text-[#F5F5F0] truncate">{a.label}</div>
              <div className="mt-1 font-body text-xs text-[#8A8A82]">
                Target <b className="text-[#F5F5F0]">{formatINR(Math.round(a.target_price))}</b> · Set at {formatINR(Math.round(a.reference_price))} · {a.unit}
              </div>
              <div className="mt-2">
                <StatusChip status={a.status} triggeredPrice={a.triggered_price} unit={a.unit} />
              </div>
            </div>
            <button
              type="button"
              onClick={() => remove(a.id)}
              className="shrink-0 text-[#8A8A82] hover:text-[#FF5A5F] transition-colors"
              aria-label="Remove alert"
              data-testid={`price-alert-remove-${a.id}`}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
